import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ActiveIncident, RecentlyResolvedIncident } from "@/client/incident";
type UseDetectRecentlyResolvedResult = {
  /** ユーザに復旧バナーとして見せるべき incident のリスト */
  resolvedToShow: RecentlyResolvedIncident[];
  /** バナー閉じる操作。指定 id のバナーを非表示にする */
  dismiss: (id: string) => void;
};

/**
 * このセッションで一度でも active として観測した incident が、
 * active から消えて recentlyResolved に出現した場合に復旧バナー対象として収集する。
 *
 * backend の active / recentlyResolved は結果整合性の GSI から別々の Query で取得されるため、
 * resolve 直後に「active からは消えたが recentlyResolved にはまだ出ていない」ポーリングが
 * 挟まりうる。「直前のポーリングで active だったか」を基準にすると、その隙間のポーリングで
 * 観測履歴が失われ復旧バナーを取りこぼす。そのため「このセッションで一度でも active だったか」
 * (seenActiveIds) を判定基準にして取りこぼしを防ぐ。
 *
 * ユーザがそもそも認知していなかった incident（最初のポーリングですでに resolved）は対象外。
 * 一度収集した id は handledIds に記録し、dismiss 後に recentlyResolved に残っていても再表示しない。
 */
export const useDetectRecentlyResolved = (active, recentlyResolved) => {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const seenActiveIdsRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handledIdsRef = useRef(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [resolvedToShow, setResolvedToShow] = useState(t2);
  let t3;
  let t4;
  if ($[3] !== active || $[4] !== recentlyResolved) {
    t3 = () => {
      for (const a of active) {
        seenActiveIdsRef.current.add(a.id);
      }
      const currentActiveIds = new Set(active.map(_temp));
      const additions = recentlyResolved.filter(r => seenActiveIdsRef.current.has(r.id) && !currentActiveIds.has(r.id) && !handledIdsRef.current.has(r.id));
      if (additions.length === 0) {
        return;
      }
      for (const a_1 of additions) {
        handledIdsRef.current.add(a_1.id);
      }
      setResolvedToShow(prev => [...prev, ...additions]);
    };
    t4 = [active, recentlyResolved];
    $[3] = active;
    $[4] = recentlyResolved;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = id => {
      setResolvedToShow(prev_0 => prev_0.filter(p => p.id !== id));
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const dismiss = t5;
  let t6;
  if ($[8] !== resolvedToShow) {
    t6 = {
      resolvedToShow,
      dismiss
    };
    $[8] = resolvedToShow;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
function _temp(a_0) {
  return a_0.id;
}